/*
    Channel holdouts (MPN-2060): each channel's exposed arm beside its held-out arm, per
    population and Target Action.

    Conversions are counted from the day each channel's test started, not from the campaign's
    go-live date (MPN-2183) -- a channel added mid-campaign would otherwise be credited with
    months of conversions from before it existed, in both arms. The Channel column says which
    day that is, so the row and the population page agree on what "since" means.

    A section of its own, never pooled into the tiles or rollups above. Those are population
    lift -- treatment against the control group -- and a channel experiment counted in there
    would be read as lift from the whole programme. Both arms here are treatment: exposed was
    served on the channel, held out was served on everything else.
*/
import type { ChannelHoldoutRow } from "@services/analytics/holdoutReportService";

import ColumnInfo from "./ColumnInfo";
import { PowerPill, TierPill } from "./StatusPill";
import { pp, pValue, rate, relativeLift, sizePair } from "./holdoutReport";

/** Date-only string, read as local midnight so it does not slip a day in a western zone. */
const day = (value: string) =>
    new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

export default function ChannelHoldoutResults({ rows }: { rows: ChannelHoldoutRow[] }) {
    return (
        <section>
            <div className="holdout-report__section-head">
                <div className="holdout-report__section-title">
                    <h2>Channel Holdouts</h2>
                </div>
                <p className="holdout-report__caption">
                    Lift from one channel: treatment served on it against treatment held out of
                    it. Separate from the population lift above, and not counted in it.
                </p>
            </div>
            <div className="holdout-report__table-wrap">
                <table className="holdout-report__table">
                    <thead>
                        <tr>
                            <th>Client<ColumnInfo column="channel_grain" /></th>
                            <th>Population<ColumnInfo column="channel_grain" /></th>
                            <th>Channel<ColumnInfo column="channel_grain" /></th>
                            <th>Target action<ColumnInfo column="channel_grain" /></th>
                            <th className="is-num">
                                Exposed / held out<ColumnInfo column="channel_arms" />
                            </th>
                            <th className="is-num">
                                Exposed conv.<ColumnInfo column="channel_rate" />
                            </th>
                            <th className="is-num">
                                Held out conv.<ColumnInfo column="channel_rate" />
                            </th>
                            <th className="is-num">Abs. lift<ColumnInfo column="absolute_lift" /></th>
                            <th className="is-num">Relative lift<ColumnInfo column="relative_lift" /></th>
                            <th>Confidence tier<ColumnInfo column="tier" /></th>
                            <th className="is-num">P-value<ColumnInfo column="p_value" /></th>
                            <th>Power<ColumnInfo column="statistical_power" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={`${row.population_segment_id}-${row.channel}-${row.journey_step_id}`}>
                                <td className="is-name">{row.client}</td>
                                <td className="is-name">{row.population}</td>
                                <td>
                                    {row.channel_label} ({row.pct}%)
                                    {row.channel_start && (
                                        <span className="holdout-report__since">
                                            since {day(row.channel_start)}
                                        </span>
                                    )}
                                </td>
                                <td className="is-name">{row.target_action}</td>
                                <td className="is-num">{sizePair(row.exposed_size, row.held_out_size)}</td>
                                <td className="is-num">{rate(row.exposed_rate)}</td>
                                <td className="is-num">{rate(row.held_out_rate)}</td>
                                <td className="is-num">{pp(row.absolute_lift)}</td>
                                <td className="is-num">{relativeLift(row.relative_lift, row.held_out_rate)}</td>
                                <td><TierPill tier={row.tier} /></td>
                                <td className="is-num">{pValue(row.p_value)}</td>
                                <td><PowerPill power={row.statistical_power} /></td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={12} className="is-empty">No channel holdouts running.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
